<ng-container *transloco="let t">
  <div class="admin-card">
    <app-admin-actions-header
            (onExportToPdf)="downloadLeagues('pdf')"
            (onExportToXls)="downloadLeagues('xlsx')"
    >
      <div slot="leadingWidgets">
        <div class="d-flex flex-column flex-xxl-row">

          <div class="me-2 mb-3 mb-md-0">
            <app-search-rounded
                    [name]="name"
                    [placeholder]="t('explore.search')"
                    (searchEmitter)="handleSearchInput($event)"
            >
            </app-search-rounded>
          </div>

          <div class="w-216 d-flex align-items-center pe-2">
            <app-select
                    [hasDefault]="true"
                    [items]="leaguesStatuses"
                    [ngModel]="leagueStatusId"
                    [defaultValue]="undefined"
                    [defaultLabel]="t('app.common.all')"
                    [placeholder]="t('app.common.status')"
                    [valueKey]="'id'"
                    [labelKey]="'id'"
                    labelKeyPrefix="league.item.status."
                    (ngModelChange)="onLeagueStatusChange($event)"
                    [fullWidthDropdown]="true"
            />
          </div>

          <div class="w-216 d-flex align-items-center">
            <app-select
                    [hasDefault]="true"
                    [items]="visibilityStatuses"
                    [ngModel]="leagueVisibilityStatusId"
                    [defaultValue]="undefined"
                    [defaultLabel]="t('app.common.all')"
                    [placeholder]="t('league.visibilityStatus')"
                    [valueKey]="'id'"
                    [labelKey]="'id'"
                    labelKeyPrefix="league.visibilityStatus."
                    (ngModelChange)="onLeagueVisibilityStatusChange($event)"
                    [fullWidthDropdown]="true"
            />
          </div>

          @if (isAdministrator) {
          <div class="w-216 d-flex align-items-center">
            <app-select
              [hasDefault]="true"
              [items]="organisers"
              [ngModel]="organiserId"
              class="ms-md-2 ms-0 mt-md-0 mt-3"
              [defaultValue]="undefined"
              [defaultLabel]="t('app.common.all')"
              [placeholder]="t('app.common.organiser')"
              [valueKey]="'id'"
              [labelKey]="'name'"
              (ngModelChange)="onOrganiserChange($event)"
              [fullWidthDropdown]="true"
            />
          </div>
          }

          <div class="ms-2">
            <app-rounded-button
                    [btnClass]="'rounded-button--muted rounded-button--sm fixed-height'"
                    (onClick)="onRefreshFilters()"
            >
              <i class="fas fa-trash-alt"></i>
            </app-rounded-button>
          </div>

        </div>
      </div>

      <div slot="trailingWidgets">
        <div class="pe-3">
          <app-rounded-button
                  [label]="t('leagues.add.new.league')"
                  (click)="goToAddLeague()"
          >
            <i class="bi bi-plus-circle pe-2"></i>
          </app-rounded-button>
        </div>
      </div>
    </app-admin-actions-header>

    <div>
      <app-loader [isLoading]="isLoading"></app-loader>

      @if ((leagues.length ?? 0) > 0) {
      <div infinite-scroll [infiniteScrollDistance]="1" (scrolled)="loadMore()">
        <ng-template [ngTemplateOutlet]="webList"></ng-template>
        <ng-template [ngTemplateOutlet]="mobileList"></ng-template>
      </div>
      } @else {
      <div class="pt-2 pb-4">
        <app-empty-state/>
      </div>
      }

      @if (showFabButton && (leagues?.length ?? 0) > 0) {
      <div (click)="scrollToTop()" class="scroll-to-top">
        <i class="bi bi-arrow-up"></i>
      </div>
      }
    </div>
  </div>

  <!-- Web Table Template -->
  <ng-template #webList>
    <div class="table-responsive dark-header d-xl-block d-none">
      <app-sortable-table
              [infiniteScrollDistance]="4"
              cssClass="table table-last-row-no-border"
              infinite-scroll
              [items]="leagues"
      >
        <thead>
        <tr>
          <th scope="col" [sortBy]="['id']" class="id-column-width">{{ t("league.id") }}</th>
          <th scope="col" [sortBy]="['name']">{{ t("league.name") }}</th>
          @if (isAdministrator) {
          <th [sortBy]="['organiser.name']">{{ t("league.organiser.name") }}</th>
          }
          <th scope="col" [sortBy]="['status']" class="status-column-width">{{ t('league.status') }}</th>
          <th scope="col" [sortBy]="['visibilityStatus']" class="visibility-column-width">{{ t('league.visibilityStatus') }}</th>
          <th scope="col" [sortBy]="['created']" data-type="date" class="date-column-width">{{ t("app.common.created") }}</th>
          <th scope="col" [sortBy]="['modified']" data-type="date" class="date-column-width">{{ t("app.common.modified") }}</th>
          <th class="more-column-width"></th>
        </tr>
        </thead>

        <tr app-table-header-separator></tr>

        <tbody>
        @for (league of leagues; track league) {
        <tr>
          <td class="center-align">{{ league.id }}</td>
            <td class="center-align">
            <a class="text-black fw-semibold" (click)="goToLeagueManagement(league)">
              <app-language-output
                [hrLabel]="league?.name"
                [enLabel]="league?.nameEng"
                [itLabel]="league?.nameIta"
                [srLabel]="league?.nameSr"
                [bsLabel]="league?.nameBs"
                [siLabel]="league?.nameSlo"
                [languages]="languages"
              />
             </a>
            </td>
            @if (isAdministrator) {
        <td class="center-align">{{ league.organiser?.name }}</td>
        }
        <td class="center-align">
          <div class="status-badge" [ngClass]="{
        'accepted': league.status === LeagueStatus.ACTIVE,
        'denied': league.status === LeagueStatus.FINISHED,
        'on-hold': league.status === LeagueStatus.INACTIVE
        }">
                <span class="fw-bold">{{ t("league.item.status." + league.status) | uppercase }}</span>
              </div>
            </td>
            <td class="center-align">
              <div class="status-badge" [ngClass]="{
        'accepted': league.visibilityStatus === VisibilityStatus.VISIBLE,
        'on-hold': league.visibilityStatus === VisibilityStatus.REQUEST_SENT,
        'publishing': league.visibilityStatus === VisibilityStatus.HIDDEN
        }">
                <span class="fw-bold">{{ t("league.visibilityStatus." + league.visibilityStatus) | uppercase }}</span>
              </div>
            </td>
            <td class="center-align">{{ league.created | localizedDate:'short' }}</td>
            <td class="center-align">{{ league.modified | localizedDate:'short' }}</td>
            <td class="text-end center-align">
              <div ngbDropdown class="dropdown dropdown--remove-arrow" container="body">
                <span class="menu-icon" id="eventActionsDropdown" ngbDropdownToggle>
                  <i class="fas fa-ellipsis-v"></i>
                </span>
                <ul aria-labelledby="eventActionsDropdown" ngbDropdownMenu>
                  <li ngbDropdownItem (click)="goToLeagueDetails(league)">{{ t("app.common.details") }}</li>
                  @if (league.visibilityStatus === VisibilityStatus.HIDDEN) {
                      <li ngbDropdownItem (click)="openPublishLeagueDialog(league)">{{ t("leagues.publish") }}</li>
                  }
                  @if (league.visibilityStatus === VisibilityStatus.VISIBLE) {
                    <li ngbDropdownItem (click)="openHideLeagueDialog(league)">{{ t("leagues.hide") }}</li>
                  }
                  <li ngbDropdownItem (click)="goToCopyLeague(league)">{{ t("app.common.copy") }}</li>
                  <li ngbDropdownItem (click)="openDeleteLeagueDialog(league)">{{ t("app.common.delete") }}</li>
                </ul>
              </div>
            </td>
          </tr>
        }
        </tbody>
      </app-sortable-table>
    </div>
  </ng-template>

  <!-- Mobile Table Template -->
  <ng-template #mobileList>
    @for (league of leagues; let isLast = $last; track league) {
    <div class="d-block d-xl-none">
      <div [class.isLastRow]="isLast" class="d-flex justify-content-center align-items-center mobile-table-row">
        <div class="flex-grow-1">
          <div [class.isLastRow]="isLast" class="d-flex flex-column">
            <div><strong>{{ t("league.id") }}:</strong> {{ league.id }}</div>
            <div>
              <strong>{{ t("league.name") }}:</strong>
              <app-language-output
                [hrLabel]="league?.name"
                [enLabel]="league?.nameEng"
                [itLabel]="league?.nameIta"
                [bsLabel]="league?.nameBs"
                [srLabel]="league?.nameSr"
                [siLabel]="league?.nameSlo"
                [languages]="languages"
              />
            </div>
            @if (isAdministrator) {
    <div><strong>{{ t("league.organiser.name") }}:</strong> {{ league.organiser?.name }}</div>
    }
    <div>
      <strong>{{ t("league.dateCreated") }}:</strong> {{ league.created | localizedDate:'short' }}
    </div>
  </div>
</div>
<div>
  <div ngbDropdown class="dropdown dropdown--remove-arrow" container="body">
    <span class="menu-icon" id="eventActionsDropdownMobile" ngbDropdownToggle>
      <i class="fas fa-ellipsis-v"></i>
    </span>
    <ul aria-labelledby="eventActionsDropdownMobile" ngbDropdownMenu>
      <li ngbDropdownItem (click)="goToLeagueDetails(league)">{{ t("app.common.details") }}</li>
              @if (league.visibilityStatus === VisibilityStatus.HIDDEN) {
    <li ngbDropdownItem (click)="openPublishLeagueDialog(league)">{{ t("leagues.publish") }}</li>
    }
    @if (league.visibilityStatus === VisibilityStatus.VISIBLE) {
    <li ngbDropdownItem (click)="openHideLeagueDialog(league)">{{ t("leagues.hide") }}</li>
    }
    <li ngbDropdownItem (click)="openDeleteLeagueDialog(league)">{{ t("app.common.delete") }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    }
  </ng-template>
</ng-container>
